// src/pages/Payment/SuccessPage.jsx
// ✅ Página de sucesso no pagamento
// Criado em: 16/03/2026
// Versão: 1.0

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  CheckCircle, 
  ArrowRight, 
  Download, 
  Mail, 
  Home,
  FileText,
  Share2,
  Award,
  Sparkles
} from 'lucide-react';

import { usePayment } from '../../contexts/PaymentContext/PaymentContext';
import { useAuth } from '../../contexts/useAuth';
import { formatBRL } from '../../utils/priceFormatter';
import { getPlanName } from '../../services/payment/planos';

/**
 * Página de sucesso após pagamento
 * Mostra confirmação e próximos passos
 */
export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const payment = usePayment();
  
  const [countdown, setCountdown] = useState(10);
  const [paymentDetails, setPaymentDetails] = useState(null);

  // Extrair parâmetros da URL
  const paymentId = searchParams.get('payment_id') || searchParams.get('id');
  const planId = searchParams.get('plan') || payment.currentPlan;
  const method = searchParams.get('method') || 'credit_card';

  // ============================================
  // 🔄 CARREGAR DETALHES DO PAGAMENTO
  // ============================================
  useEffect(() => {
    const loadPaymentDetails = async () => {
      // Simular detalhes do pagamento
      const mockDetails = {
        id: paymentId || `pay_${Date.now()}`,
        plan: planId || 'professional',
        amount: 197,
        date: new Date().toISOString(),
        method: method,
        lastDigits: method === 'credit_card' ? '4242' : null,
        receiptUrl: '#',
        nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      };

      setPaymentDetails(mockDetails);
    };

    loadPaymentDetails();
    
    // Recarregar plano do usuário
    payment.refreshPlan();
  }, [paymentId, planId, method, payment]);

  // ============================================
  // ⏱️ CONTADOR PARA REDIRECIONAMENTO
  // ============================================
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  // ============================================
  // 🎯 AÇÕES
  // ============================================

  const handleDownloadReceipt = () => {
    // TODO: Gerar comprovante PDF
    console.log('Download comprovante');
  };

  const handleShare = () => {
    // Compartilhar nas redes
    const text = `Acabei de assinar o plano ${getPlanName(planId)} no Imóveis Pro! 🏠`;
    if (navigator.share) {
      navigator.share({
        title: 'Imóveis Pro',
        text: text,
        url: window.location.origin
      });
    }
  };

  const handleGoToDashboard = () => {
    navigate('/');
  };

  // ============================================
  // 🎨 RENDERIZAÇÃO
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center p-4">
      
      {/* Card principal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden"
      >
        
        {/* Header com gradiente */}
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-8 text-center relative overflow-hidden">
          {/* Círculos decorativos */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/10 rounded-full" />
          
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
            className="w-20 h-20 bg-white rounded-full mx-auto mb-4 flex items-center justify-center shadow-xl"
          >
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </motion.div>
          
          <h1 className="text-3xl font-bold text-white mb-2">
            Pagamento confirmado!
          </h1>
          <p className="text-emerald-100">
            Obrigado por assinar o Imóveis Pro
          </p>
        </div>

        {/* Conteúdo */}
        <div className="p-8">
          
          {/* Badge de boas-vindas */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 flex items-center gap-3"
          >
            <Sparkles className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="font-medium text-emerald-900">
                Bem-vindo ao plano {getPlanName(planId)}!
              </p>
              <p className="text-sm text-emerald-700">
                Sua assinatura já está ativa. Aproveite todos os recursos.
              </p>
            </div>
          </motion.div>

          {/* Detalhes do pagamento */}
          {paymentDetails && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-slate-50 rounded-xl p-6 mb-6"
            >
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Detalhes da transação
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">ID do pagamento:</span>
                  <span className="font-mono text-slate-900">{paymentDetails.id}</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Plano:</span>
                  <span className="font-medium text-slate-900">
                    {getPlanName(paymentDetails.plan)}
                  </span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Valor:</span>
                  <span className="font-bold text-emerald-600">
                    {formatBRL(paymentDetails.amount)}
                  </span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Forma de pagamento:</span>
                  <span className="text-slate-900">
                    {paymentDetails.method === 'credit_card' 
                      ? `Cartão final ${paymentDetails.lastDigits}`
                      : paymentDetails.method === 'pix'
                        ? 'PIX'
                        : 'Boleto'}
                  </span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Data:</span>
                  <span className="text-slate-900">
                    {new Date(paymentDetails.date).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Próxima cobrança:</span>
                  <span className="text-slate-900">
                    {new Date(paymentDetails.nextBillingDate).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Ações */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="grid grid-cols-2 gap-3 mb-6"
          >
            <button
              onClick={handleDownloadReceipt}
              className="p-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm">Comprovante</span>
            </button>
            
            <button
              onClick={handleShare}
              className="p-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition flex items-center justify-center gap-2"
            >
              <Share2 className="w-4 h-4" />
              <span className="text-sm">Compartilhar</span>
            </button>
          </motion.div>

          {/* Email de confirmação */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="bg-blue-50 rounded-xl p-4 mb-6 flex items-start gap-3"
          >
            <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900">
                Enviamos um email de confirmação para <strong>{user?.email}</strong>
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Verifique sua caixa de entrada e spam
              </p>
            </div>
          </motion.div>

          {/* Botão principal */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleGoToDashboard}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
          >
            <Home className="w-5 h-5" />
            Ir para o Dashboard
          </motion.button>

          {/* Redirecionamento automático */}
          <p className="text-center text-sm text-slate-400 mt-4">
            Redirecionando em <span className="font-bold text-slate-600">{countdown}</span> segundos...
          </p>
        </div>
      </motion.div>
    </div>
  );
}